<script lang="ts">
import { defineComponent } from 'vue';
import TableFiller from '@/components/table-filler.vue';
import { formatDate, formatMoney, formatCurrency } from '@/utils/format';
import Decimal from 'decimal.js-light';
import ClipboardIcon from '@/components/clipboard-icon.vue';
import { usePoolsStore } from '@/stores/pools';
import { mapState } from 'pinia';
import { fetchPool } from '@/api/admin-be';
import { catchError } from '@/helpers/toast';

export default defineComponent({
  name: 'bets-list',
  components: { ClipboardIcon, TableFiller },
  computed: {
    ...mapState(usePoolsStore, ['pools', 'currentPoolId']),
    betTotals() {
      const zeroes = () => ({
        stake: new Decimal(0),
        stakeUsd: new Decimal(0),
        win: new Decimal(0),
        winUsd: new Decimal(0),
        jackpot: new Decimal(0),
        jackpotUsd: new Decimal(0),
        currencies: [] as string[],
      });
      const stakes: { [id: number]: ReturnType<typeof zeroes> } = {};

      for (const bet of this.bets || []) {
        if (!stakes[bet.id]) {
          stakes[bet.id] = zeroes();
        }

        for (const tx of bet.poolBetTransactions) {
          switch (tx.type) {
            case 'Stake':
              if (tx.amount > 0) {
                stakes[bet.id].currencies.push(tx.currency);
                stakes[bet.id].stake = stakes[bet.id].stake.plus(tx.amount);
                stakes[bet.id].stakeUsd = stakes[bet.id].stakeUsd.plus(tx.vendorAmount);
              } else {
                stakes[bet.id].win = stakes[bet.id].win.minus(tx.amount);
                stakes[bet.id].winUsd = stakes[bet.id].winUsd.minus(tx.vendorAmount);
              }
              break;
            case 'Jackpot':
              stakes[bet.id].jackpot = stakes[bet.id].jackpot.minus(tx.amount);
              stakes[bet.id].jackpotUsd = stakes[bet.id].jackpotUsd.minus(tx.vendorAmount);
              break;
          }
        }
      }

      return stakes;
    },
  },
  data() {
    return {
      openBets: new Set<number>(),
      pool: null,
    };
  },
  methods: {
    formatDate,
    formatMoney,
    formatCurrency,
    toggleBet(id: number) {
      const method = this.openBets.has(id) ? 'delete' : 'add';
      this.openBets[method](id);
    },
    sorter2(a, b) {
      const aT = new Date(a.market.Fixture.StartTimeUtc).getTime();
      const bT = new Date(b.market.Fixture.StartTimeUtc).getTime();

      return aT - bT;
    },
    sorter3(a, b) {
      return 0;
    },
    findLeg(selectionId: number, bet) {
      return bet.poolBetLegs.find((leg) => leg.selection.Id === selectionId);
    },
    resultStatus({ ResultStatus }: { ResultStatus: string }) {
      let icon: string;
      switch (ResultStatus) {
        case 'Placed':
        case 'Winner':
          icon = 'text-green-500';
          break;
        case 'Partial':
          icon = 'text-yellow-500';
          break;
        case 'Loser':
          icon = 'text-black dark:text-white';
          break;
        default:
          icon = 'text-gray-500';
          break;
      }

      return `px-2 py-1 ${icon}`;
    },
    won(selection: { ResultStatus: string; AdditionalResultData: { PercentageWin: number } }) {
      if (!selection) return false;
      return (
        selection.ResultStatus === 'Winner' ||
        selection.ResultStatus === 'Placed' ||
        (selection.ResultStatus === 'Partial' && selection.AdditionalResultData.PercentageWin > 0)
      );
    },
    winCategory(bet) {
      const markets = new Set(bet.poolBetLegs.filter((l) => this.won(l.selection)).map((l) => l.selection.Market.Id))
      return markets.size;
    },
    countGuessed(bet: any) {
      let t = 0;
      for (const { selection } of bet.poolBetLegs) {
        if (!selection.ResultStatus || selection.ResultStatus === 'Loser') {
          console.log(selection.Market.Fixture.Name, selection.Name, 'skip');
          continue;
        }
        if (
          ['Winner', 'Pushed', 'Placed'].includes(selection.ResultStatus) ||
          (selection.ResultStatus === 'Partial' && selection.AdditionalResultData.PercentageWin > 0)
        ) {
          console.log(selection.Market.Fixture.Name, selection.Name, 'Win');
          t++;
        }
      }
      return t || null;
    },
    fetchPool() {
      if (this.currentPoolId) {
        fetchPool(this.currentPoolId)
          .then((pool) => {
            console.log('POOL', pool);
            pool.poolEvents.sort(this.sorter2);
            this.pool = pool;
          })
          .catch((error) => catchError('Ошибка загрузки пула', error));
      } else {
        this.pool = null;
      }
    },
  },
  mounted() {
    this.fetchPool();
  },
  props: ['bets', 'pageSize'],
});
</script>

<template>
  <table class="basic-table" v-if="bets">
    <thead>
      <tr>
        <th style="width: 80px" rowspan="2">ID</th>
        <th rowspan="2">Игрок</th>
        <th rowspan="2">Дата</th>
        <th rowspan="2">Категория</th>
        <th rowspan="2">Угадано</th>
        <th colspan="4">Валюта игрока</th>
        <th colspan="3">USD</th>
      </tr>
      <tr>
        <th>Валюта</th>
        <th class="!text-right">Ставка</th>
        <th class="!text-right">Выигрыш</th>
        <th class="!text-right">Джекпот</th>
        <th class="!text-right">Ставка</th>
        <th class="!text-right">Выигрыш</th>
        <th class="!text-right">Джекпот</th>
      </tr>
    </thead>
    <tbody>
      <template v-for="bet in bets" :key="bet.id">
        <tr @click="toggleBet(bet.id)">
          <td>{{ bet.id }}</td>
          <td class="text-nowrap">
            <router-link
              :to="{ name: 'detailed-bets', query: { userId: bet.userId } }"
              class="text-blue-500 underline"
            >
              {{ bet.userId }}
            </router-link>
            <clipboard-icon :text="bet.userId" iconClass="text-[10px] ml-1" />
            <router-link :to="{ name: 'profile', params: { id: bet.userId } }">
              <i
                class="pi pi-external-link ml-1 text-[10px] opacity-40 hover:opacity-100 cursor-pointer"
              />
            </router-link>
          </td>
          <td class="text-nowrap">
            {{ formatDate(bet.createdAt, 'DD.MM.YYYY') }}
            <span class="text-gray-500">{{ formatDate(bet.createdAt, 'HH:mm') }}</span>
          </td>

          <td>{{ winCategory(bet) }}</td>
          <td>{{ countGuessed(bet) }} / {{ bet.poolBetLegs.length }}</td>

          <td>
            {{
              betTotals[bet.id].currencies
                .map((c) => formatCurrency(betTotals[bet.id].stake.toNumber(), c))
                .join(' | ')
            }}
          </td>
          <td class="!text-right font-mono">
            {{ formatMoney(betTotals[bet.id].stake.toNumber(), 4) }}
          </td>
          <td class="!text-right font-mono">
            {{ formatMoney(betTotals[bet.id].win.toNumber(), 4) || '' }}
          </td>
          <td class="!text-right font-mono">
            {{ formatMoney(betTotals[bet.id].jackpot.toNumber(), 4) || '' }}
          </td>

          <td class="!text-right font-mono">
            {{ formatMoney(betTotals[bet.id].stakeUsd.toNumber(), 2, false) }}
          </td>
          <td class="!text-right font-mono">
            {{ formatMoney(betTotals[bet.id].winUsd.toNumber(), 2, false) || '' }}
          </td>
          <td class="!text-right font-mono">
            {{ formatMoney(betTotals[bet.id].jackpotUsd.toNumber(), 2, false) || '' }}
          </td>
        </tr>
        <tr v-show="openBets.has(bet.id)">
          <td colspan="1000">
            <table>
              <tr v-for="(event, i) in pool.poolEvents" :key="event.id">
                <td>{{ i + 1 }}</td>
                <td class="!text-left">
                  {{ formatDate(event.market.Fixture.StartTimeUtc, 'DD.MM.YYYY HH:mm') }}
                </td>
                <td class="!text-left">{{ event.market.Fixture.Name }}</td>
                <td class="!text-left">{{ event.market.Name }}</td>
                <td>
                  <i
                    :class="
                      bet.poolBetLegs.some(
                        (l) => won(l.selection) && l.selection.Market.Id === event.market.Id
                      )
                        ? 'pi pi-check text-green-500'
                        : 'pi pi-times text-red-500'
                    "
                  />
                </td>
                <td
                  v-for="selection in event.market.Selections.sort(sorter3)"
                  :key="selection.Id"
                  :class="{
                    '!text-left': true,
                    'bg-gray-200 dark:bg-gray-800': findLeg(selection.Id, bet),
                  }"
                >
                  <span :class="resultStatus(selection)"> {{ selection.Name }} </span>
                </td>
              </tr>
            </table>
          </td>
        </tr>
      </template>
      <table-filler :max="pageSize" :rows="bets" />
    </tbody>
  </table>
</template>

<style scoped lang="scss"></style>
